import { OrderStatus } from "../enums";

/** Enquiry statuses before the customer has responded to the draft estimate. */
const PRE_DRAFT_ESTIMATE_RESPONSE_STATUSES: string[] = [
  OrderStatus.NEW,
  OrderStatus.ACK,
  OrderStatus.PART_CONVERSION_STARTED,
  OrderStatus.PART_CONVERSION_COMPLETED,
  OrderStatus.DRAFT_ESTIMATE_SHARED,
];

export const SUPPLIER_DISCOVERY_AWAITING_DRAFT_ESTIMATE_RESPONSE_HINT =
  "Supplier discovery opens once the customer responds to the draft estimate";

const isEnquiryRoot = (order: any): boolean =>
  order?.requireEstimate === true &&
  Boolean(order?._id) &&
  String(order?.rootOrderId) === String(order?._id);

/**
 * True when supplier discovery must stay disabled because the enquiry needs a
 * price estimate (`requirePriceEstimate`) and the customer has not responded to
 * the draft estimate yet: up to DRAFT_ESTIMATE_SHARED, including a customer
 * hold taken from DRAFT_ESTIMATE_SHARED. The response moves the enquiry to
 * FINAL_ESTIMATE_REQUESTED (final estimate states on or off), and from there on
 * it is false. Enquiries without the flag and child orders are never affected, so
 * the existing discovery rules apply to them unchanged.
 */
export const isSupplierDiscoveryAwaitingDraftEstimateResponse = (
  order: any,
): boolean => {
  if (order?.requirePriceEstimate !== true || !isEnquiryRoot(order)) {
    return false;
  }
  if (PRE_DRAFT_ESTIMATE_RESPONSE_STATUSES.includes(order.orderStatus)) {
    return true;
  }
  return (
    order.orderStatus === OrderStatus.CUSTOMER_HOLD &&
    order.metaInfo?.customerHoldReturnState ===
      OrderStatus.DRAFT_ESTIMATE_SHARED
  );
};

export const AUTO_FINAL_ESTIMATE_HINT =
  "The final estimate for this enquiry is generated automatically";

/**
 * True when ORCA's manual enquiry actions must stay disabled (supplier
 * discovery, part add / cancel / hold / unhold / CBO, R2D, CBO, Customer Hold,
 * Void) because the backend final estimate flow has started for this enquiry (`metaInfo.autoFinalEstimateStartedTime`,
 * stamped on entering FINAL_ESTIMATE_REQUESTED, so it covers estimates still being
 * built as well as shared ones) and mobile config
 * `ff_disable_manual_estimate_generation` is on. Enquiries the backend never
 * estimated and child orders are never affected.
 */
export const isManualActionBlockedByAutoFinalEstimate = (
  order: any,
  ffDisableManualEstimateGeneration: unknown,
): boolean =>
  (ffDisableManualEstimateGeneration === true ||
    ffDisableManualEstimateGeneration === "true") &&
  isEnquiryRoot(order) &&
  Boolean(order?.metaInfo?.autoFinalEstimateStartedTime);
